import { useState, useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useApiMutation } from '@/hooks/useApiMutation'
import { useToast } from '@/components/ToastContainer'
import { webUrl } from '@/lib/api'
import { useAdminFilters } from '@/hooks/useAdminFilters'
import { Input, Select, Table, Pagination, Alert, Badge, Button, type TableColumn } from '@/ui'
import { StatusBadge } from '@/components/StatusBadge'
import { formatDate, formatNumber } from '@/ui'
import styles from './Invitations.module.css'

interface AdminInvitation {
  id: string
  user: { id: string; email: string; name: string | null }
  groomName: string | null
  brideName: string | null
  weddingDate: string | null
  slug: string | null
  status: string
  activated: boolean
  viewCount: number
  templateId: string | null
  createdAt: string
  publishedAt: string | null
  expiresAt: string | null
}

interface InvitationsResponse {
  invitations: AdminInvitation[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
}

const STATUS_OPTIONS = [
  { value: '', label: '전체 상태' },
  { value: 'PUBLISHED', label: 'PUBLISHED (발행됨)' },
  { value: 'EXPIRED', label: 'EXPIRED (만료)' },
  { value: 'DELETED', label: 'DELETED (삭제)' },
]

const DAY_MS = 24 * 60 * 60 * 1000

function relativeDays(iso: string | null, now: Date): { text: string; expired: boolean } | null {
  if (!iso) return null
  const target = new Date(iso).getTime()
  const diff = target - now.getTime()
  const days = Math.round(diff / DAY_MS)
  if (days > 0) return { text: `D-${days}`, expired: false }
  if (days === 0) return { text: '오늘', expired: false }
  return { text: `D+${Math.abs(days)}`, expired: true }
}

export function InvitationsListTab() {
  const { filters, setFilters, resetFilters } = useAdminFilters()
  const { addToast } = useToast()
  const [searchParams] = useSearchParams()
  // 초기 필터값을 URL search params 에서 읽음 (드릴다운 진입 지원).
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') ?? '')
  const [searchInput, setSearchInput] = useState('')
  const [togglingId, setTogglingId] = useState<string | null>(null) // 행별 토글 스피너
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    resetFilters()
  }, [resetFilters])

  const params = new URLSearchParams()
  params.set('page', String(filters.page))
  params.set('pageSize', String(filters.pageSize))
  params.set('sortBy', filters.sortBy)
  params.set('sortOrder', filters.sortOrder)
  if (filters.search) params.set('userId', filters.search)
  if (statusFilter) params.set('status', statusFilter)

  const { data, isLoading, error, refetch } = useApiFetch<InvitationsResponse>(
    `/api/admin/invitations?${params.toString()}`,
  )

  const { trigger } = useApiMutation('/api/admin/invitations')

  // 청첩장 단건 활성화/비활성화 (어드민 오버라이드 — 슬롯 미차감)
  const toggleActivation = useCallback(
    async (row: AdminInvitation) => {
      const next = !row.activated
      setTogglingId(row.id)
      const result = await trigger(
        { activate: next },
        { pathOverride: `/api/admin/invitations/${row.id}/activation` },
      )
      setTogglingId(null)
      if (result) {
        addToast({ type: 'success', message: next ? '활성화되었습니다.' : '비활성화되었습니다.' })
        refetch()
      } else {
        addToast({ type: 'error', message: '처리에 실패했습니다.' })
      }
    },
    [trigger, addToast, refetch],
  )

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value
      setSearchInput(val)
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        setFilters({ search: val.trim() || undefined, page: 1 })
      }, 300)
    },
    [setFilters],
  )

  useEffect(() => () => clearTimeout(debounceRef.current), [])

  const handleSort = useCallback(
    (key: string, order: 'asc' | 'desc') => {
      setFilters({ sortBy: key, sortOrder: order })
    },
    [setFilters],
  )

  const handlePageChange = useCallback(
    (page: number) => {
      setFilters({ page })
    },
    [setFilters],
  )

  const now = new Date()

  const columns: TableColumn<AdminInvitation>[] = [
    {
      key: 'couple',
      label: '신랑·신부',
      width: '180px',
      render: (row) => (
        <div className={styles.couple}>
          <span>
            {row.groomName ?? '-'}
            <span className={styles.coupleHeart}>♥</span>
            {row.brideName ?? '-'}
          </span>
          <span className={styles.coupleSub}>{row.id.slice(0, 12)}…</span>
        </div>
      ),
    },
    {
      key: 'user',
      label: '유저',
      width: '200px',
      render: (row) => (
        <div className={styles.userCell}>
          <span>{row.user.name ?? row.user.email}</span>
          <span className={styles.userId}>{row.user.id}</span>
        </div>
      ),
    },
    {
      key: 'weddingDate',
      label: '예식일',
      width: '130px',
      render: (row) => {
        if (!row.weddingDate) return <span className={styles.dateRelative}>미입력</span>
        // weddingDate 는 서버가 준 KST 캘린더 날짜(YYYY-MM-DD). 파싱 밀림 방지 위해 문자열로 표시.
        const [y, m, d] = row.weddingDate.split('-')
        const rel = relativeDays(`${row.weddingDate}T12:00:00+09:00`, now)
        return (
          <div className={styles.dateCell}>
            <span>{`${y}.${Number(m)}.${Number(d)}`}</span>
            <span className={rel?.expired ? styles.dateExpired : styles.dateRelative}>{rel?.text}</span>
          </div>
        )
      },
    },
    {
      key: 'status',
      label: '발행 상태',
      width: '110px',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'activated',
      label: '활성화',
      width: '80px',
      render: (row) =>
        row.activated ? <Badge color="green">활성</Badge> : <Badge color="gray">비활성</Badge>,
    },
    {
      key: 'viewCount',
      label: '조회수',
      sortable: true,
      align: 'right' as const,
      width: '80px',
      render: (row) => formatNumber(row.viewCount),
    },
    {
      key: 'createdAt',
      label: '생성일',
      sortable: true,
      width: '140px',
      render: (row) => (
        <div className={styles.dateCell}>
          <span>{formatDate(row.createdAt, 'yyyy.M.d')}</span>
          <span className={styles.dateRelative}>{formatDate(row.createdAt, 'HH:mm')}</span>
        </div>
      ),
    },
    {
      key: 'publishedAt',
      label: '발행일',
      sortable: true,
      width: '120px',
      render: (row) =>
        row.publishedAt ? formatDate(row.publishedAt, 'yyyy.M.d') : '-',
    },
    {
      key: 'expiresAt',
      label: '만료일',
      sortable: true,
      width: '140px',
      render: (row) => {
        if (!row.expiresAt) return '-'
        const rel = relativeDays(row.expiresAt, now)
        return (
          <div className={styles.dateCell}>
            <span>{formatDate(row.expiresAt, 'yyyy.M.d')}</span>
            <span className={rel?.expired ? styles.dateExpired : styles.dateRelative}>
              {rel?.text}
            </span>
          </div>
        )
      },
    },
    {
      key: 'view',
      label: '',
      width: '60px',
      render: (row) => {
        if (row.status !== 'PUBLISHED') return null
        const path = webUrl(row.slug ? `/letter/${row.slug}` : `/inv/${row.id}`)
        return (
          <a
            href={path}
            target="_blank"
            rel="noreferrer"
            className={styles.viewLink}
          >
            보기 ↗
          </a>
        )
      },
    },
    {
      key: 'activation',
      label: '',
      width: '100px',
      render: (row) => {
        // 삭제된 청첩장은 토글 비노출
        if (row.status === 'DELETED') return null
        return (
          <Button
            size="sm"
            variant={row.activated ? 'secondary' : 'primary'}
            loading={togglingId === row.id}
            onClick={() => toggleActivation(row)}
          >
            {row.activated ? '비활성화' : '활성화'}
          </Button>
        )
      },
    },
  ]

  return (
    <>
      <div className={styles.filters}>
        <Input
          size="sm"
          placeholder="유저 ID, 이메일, 이름 검색..."
          value={searchInput}
          onChange={handleSearchChange}
          className={styles.searchInput}
        />
        <Select
          size="sm"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setFilters({ page: 1 })
          }}
          options={STATUS_OPTIONS}
        />
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table<AdminInvitation>
        columns={columns}
        data={data?.invitations ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSort={handleSort}
      />

      {data?.pagination && data.pagination.totalPages > 1 && (
        <Pagination
          current={data.pagination.page}
          total={data.pagination.totalPages}
          onChange={handlePageChange}
          size="sm"
        />
      )}
    </>
  )
}
